"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ClipboardFallbackModal = exports.ClipboardFallback = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_1 = __importDefault(require("@emotion/styled"));
const react_1 = require("react");
const ui_1 = require("react-science/ui");
const Button_1 = __importDefault(require("../../component/elements/Button"));
const ClipboardForm = styled_1.default.form `
  padding: 0.5rem;

  & > label {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  textarea {
    padding: 0.5rem;
  }
`;
const Actions = styled_1.default.div `
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;
function throwError(props, prop) {
    throw new Error(`props.${prop} is mandatory with ${String(props.mode)} mode`);
}
function assertReadProps(props) {
    if (typeof props.onRead !== 'function')
        throwError(props, 'onRead');
}
function assertReadTextProps(props) {
    if (typeof props.onReadText !== 'function')
        throwError(props, 'onReadText');
}
function assertWriteProps(props) {
    if (!props.file)
        throwError(props, 'file');
    if (typeof props.onDismiss !== 'function')
        throwError(props, 'onDismiss');
}
function assertWriteTextProps(props) {
    if (typeof props.text !== 'string')
        throwError(props, 'text');
    if (typeof props.onDismiss !== 'function')
        throwError(props, 'onDismiss');
}
/**
 * switch between ClipboardFallback variant over mode props
 * mode === read => ClipboardFallbackReadProps
 * mode === readText => ClipboardFallbackReadTextProps
 * mode === write => ClipboardFallbackWriteProps
 * mode === writeText => ClipboardFallbackWriteTextProps
 *
 * implement what you need in function what part of the clipboard api from useClipboard you use
 * props will be checked at runtime
 *
 * @param props
 */
function ClipboardFallback(props) {
    switch (props.mode) {
        case 'read':
            assertReadProps(props);
            return (0, jsx_runtime_1.jsx)(ClipboardFallbackRead, { ...props });
        case 'readText':
            assertReadTextProps(props);
            return (0, jsx_runtime_1.jsx)(ClipboardFallbackReadText, { ...props });
        case 'write': {
            assertWriteProps(props);
            return (0, jsx_runtime_1.jsx)(ClipboardFallbackWrite, { ...props });
        }
        case 'writeText':
            assertWriteTextProps(props);
            return (0, jsx_runtime_1.jsx)(ClipboardFallbackWriteText, { ...props });
        default:
            return null;
    }
}
exports.ClipboardFallback = ClipboardFallback;
/**
 * return null if !props.mode
 *
 * @param props
 * @see ClipboardFallback
 */
function ClipboardFallbackModal(props) {
    if (!props.mode)
        return null;
    const titles = {
        read: `Import data from file`,
        readText: `Paste text from clipboard`,
        write: `Export data to file`,
        writeText: `Copy text from text area`,
    };
    return ((0, jsx_runtime_1.jsxs)(ui_1.Modal, { hasCloseButton: true, isOpen: true, onRequestClose: props.onDismiss, children: [(0, jsx_runtime_1.jsx)(ui_1.Modal.Header, { children: (0, jsx_runtime_1.jsx)("h2", { children: titles[props.mode] }) }), (0, jsx_runtime_1.jsx)(ui_1.Modal.Body, { children: (0, jsx_runtime_1.jsx)(ClipboardFallback, { ...props }) })] }));
}
exports.ClipboardFallbackModal = ClipboardFallbackModal;
function ClipboardFallbackRead(props) {
    const onFileRef = (0, react_1.useRef)(props.onRead);
    onFileRef.current = props.onRead;
    const onSubmit = (0, react_1.useCallback)((event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const file = data.get('file');
        if (!(file instanceof File))
            return;
        onFileRef.current(file);
    }, []);
    if (props.mode !== 'read')
        return null;
    return ((0, jsx_runtime_1.jsxs)(ClipboardForm, { onSubmit: onSubmit, children: [(0, jsx_runtime_1.jsxs)("label", { children: [(0, jsx_runtime_1.jsx)("span", { children: props.label ?? 'File' }), (0, jsx_runtime_1.jsx)("input", { name: "file", type: "file" })] }), (0, jsx_runtime_1.jsx)("p", { children: "We were not able to read from your clipboard. Please input your data in the file field" }), (0, jsx_runtime_1.jsxs)(Actions, { children: [(0, jsx_runtime_1.jsx)(Button_1.default.Done, { type: "submit", children: "Submit" }), (0, jsx_runtime_1.jsx)(Button_1.default.Secondary, { type: "reset", onClick: props.onDismiss, children: "Cancel" })] })] }));
}
function ClipboardFallbackReadText(props) {
    const onTextRef = (0, react_1.useRef)(props.onReadText);
    onTextRef.current = props.onReadText;
    const onSubmit = (0, react_1.useCallback)((event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        const text = data.get('text');
        if (typeof text !== 'string')
            return;
        onTextRef.current(text);
    }, []);
    if (props.mode !== 'readText')
        return null;
    return ((0, jsx_runtime_1.jsxs)(ClipboardForm, { onSubmit: onSubmit, children: [(0, jsx_runtime_1.jsxs)("label", { children: [(0, jsx_runtime_1.jsx)("span", { children: props.label ?? 'Text' }), (0, jsx_runtime_1.jsx)("textarea", { name: "text", cols: 50, rows: 10 })] }), (0, jsx_runtime_1.jsx)("p", { children: "We were not able to read text from your clipboard. Please paste your data in the text area." }), (0, jsx_runtime_1.jsxs)(Actions, { children: [(0, jsx_runtime_1.jsx)(Button_1.default.Done, { type: "submit", children: "Submit" }), (0, jsx_runtime_1.jsx)(Button_1.default.Secondary, { type: "reset", onClick: props.onDismiss, children: "Cancel" })] })] }));
}
function ClipboardFallbackWrite(props) {
    const onDismissRef = (0, react_1.useRef)(props.onDismiss);
    onDismissRef.current = props.onDismiss;
    const onSubmit = (0, react_1.useCallback)((event) => {
        event.preventDefault();
        onDismissRef.current();
    }, []);
    const downloadLink = (0, react_1.useMemo)(() => URL.createObjectURL(props.file), [props.file]);
    (0, react_1.useEffect)(() => {
        return () => URL.revokeObjectURL(downloadLink);
    }, [downloadLink]);
    if (props.mode !== 'write')
        return null;
    return ((0, jsx_runtime_1.jsxs)(ClipboardForm, { onSubmit: onSubmit, children: [(0, jsx_runtime_1.jsx)("p", { children: props.label ?? 'Download' }), (0, jsx_runtime_1.jsx)("a", { href: downloadLink, download: props.file.name, children: "We were not able to write data to your clipboard. Please download your data with this link." }), (0, jsx_runtime_1.jsx)(Actions, { children: (0, jsx_runtime_1.jsx)(Button_1.default.Secondary, { type: "submit", children: "Dismiss" }) })] }));
}
function ClipboardFallbackWriteText(props) {
    const onDismissRef = (0, react_1.useRef)(props.onDismiss);
    onDismissRef.current = props.onDismiss;
    const onSubmit = (0, react_1.useCallback)((event) => {
        event.preventDefault();
        onDismissRef.current();
    }, []);
    if (props.mode !== 'writeText')
        return null;
    return ((0, jsx_runtime_1.jsxs)(ClipboardForm, { onSubmit: onSubmit, children: [(0, jsx_runtime_1.jsx)("span", { children: props.label ?? 'Text' }), (0, jsx_runtime_1.jsx)("textarea", { name: "text", cols: 30, rows: 10, ref: (node) => node?.select(), children: props.text }), (0, jsx_runtime_1.jsx)("p", { children: "We were not able to write text to your clipboard. Please copy your data in the text area." }), (0, jsx_runtime_1.jsx)(Actions, { children: (0, jsx_runtime_1.jsx)(Button_1.default.Secondary, { type: "submit", children: "Dismiss" }) })] }));
}
